"use client";

import { useLayoutEffect, useRef, useState, type CSSProperties } from "react";
import { SLIDE_W, SLIDE_H, type ClosingSlideData } from "@/lib/carousel/types";
import { minimalClosingLayout, type MinimalTextBox } from "@/lib/carousel/templates/minimal-light";

export interface MinimalLightClosingOverlayProps {
  data: ClosingSlideData;
  fonts: { display: string; body: string };
  update: (patch: Partial<{ heading: string; subtext: string; handle: string }>) => void;
}

function boxStyle(box: MinimalTextBox, fonts: { display: string; body: string }): CSSProperties {
  return {
    position: "absolute",
    left: "50%",
    top: box.top,
    transform: "translate(-50%, -50%)",
    width: SLIDE_W - 288,
    textAlign: "center",
    fontFamily: box.family === "display" ? fonts.display : fonts.body,
    fontSize: box.fontSize,
    fontWeight: box.fontWeight,
    color: box.color,
  };
}

/** Same overlay pattern as FieldDiaryClosingOverlay, for Minimal Light's
 * centered closing slide — positioned via minimalClosingLayout(), the same
 * source of truth minimal-light.ts's drawClosing uses. */
export default function MinimalLightClosingOverlay({ data, fonts, update }: MinimalLightClosingOverlayProps) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(360 / SLIDE_W);

  useLayoutEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const measure = () => {
      if (el.clientWidth > 0) setScale(el.clientWidth / SLIDE_W);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const { heading, subtext, handle } = minimalClosingLayout(data);

  return (
    <div ref={wrapRef} className="ced-text-overlay">
      <div
        className="ced-text-overlay-inner"
        style={{ width: SLIDE_W, height: SLIDE_H, transform: `scale(${scale})` }}
      >
        <input
          type="text"
          className="ced-text-overlay-input"
          style={boxStyle(heading, fonts)}
          value={data.heading}
          onChange={(e) => update({ heading: e.target.value })}
          placeholder="Heading"
          aria-label="Heading"
        />
        <input
          type="text"
          className="ced-text-overlay-input"
          style={boxStyle(subtext, fonts)}
          value={data.subtext}
          onChange={(e) => update({ subtext: e.target.value })}
          placeholder="Subtext"
          aria-label="Subtext"
        />
        {handle && (
          <input
            type="text"
            className="ced-text-overlay-input"
            style={boxStyle(handle, fonts)}
            value={data.handle}
            onChange={(e) => update({ handle: e.target.value })}
            aria-label="Handle"
          />
        )}
      </div>
    </div>
  );
}
